<template>
  <div class="account-page">
    <div class="account-shell">
      <header class="account-head">
        <button type="button" class="head-btn" :aria-label="$t('auth.back')" @click="back">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M15 18l-6-6 6-6"></path>
          </svg>
        </button>

        <div class="head-copy">
          <h1>{{ copy.title }}</h1>
          <p>{{ copy.subtitle }}</p>
        </div>

        <button type="button" class="head-btn" :aria-label="$t('auth.home')" @click="goHome">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 11l9-8 9 8"></path>
            <path d="M5 10v10h14V10"></path>
          </svg>
        </button>
      </header>

      <main class="account-content">
        <nav class="segmented">
          <button
            v-for="tab in tabs"
            :key="tab.value"
            type="button"
            :class="{ active: activeTab === tab.value }"
            @click="activeTab = tab.value"
          >
            {{ tab.label }}
          </button>
        </nav>

        <div v-if="!filteredRecords.length" class="empty-state">
          <div class="empty-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <rect x="3.5" y="7" width="3.2" height="11" rx="1"></rect>
              <rect x="8.1" y="4" width="3.2" height="14" rx="1"></rect>
              <rect x="12.7" y="8" width="3.2" height="10" rx="1"></rect>
              <rect x="17.3" y="5.5" width="3.2" height="12.5" rx="1"></rect>
            </svg>
          </div>
          <p>{{ copy.emptyText }}</p>
        </div>

        <div v-else class="record-list">
          <button
            v-for="item in filteredRecords"
            :key="`${item.type}-${item.id}`"
            type="button"
            class="record-card"
            @click="openDetail(item)"
          >
            <div class="record-head-row">
              <b>{{ item.title }}</b>
              <span class="record-status" :class="item.statusClass">
                {{ item.statusText }}
              </span>
            </div>

            <div class="record-lines">
              <div class="record-line">
                <span>{{ copy.amountLabel }}</span>
                <b :class="item.directionClass">{{ item.amountText }}</b>
              </div>
              <div class="record-line">
                <span>{{ copy.typeLabel }}</span>
                <b>{{ item.typeText }}</b>
              </div>
              <div class="record-line">
                <span>{{ copy.timeLabel }}</span>
                <b>{{ item.timeText }}</b>
              </div>
            </div>
          </button>
        </div>
      </main>
    </div>

    <div v-if="detailVisible" class="modal-mask" @click.self="closeDetail">
      <div class="modal-card">
        <h3>{{ copy.detailTitle }}</h3>
        <div class="detail-list">
          <div class="detail-row">
            <span>{{ copy.typeLabel }}</span>
            <b>{{ activeRecord ? activeRecord.typeText : "--" }}</b>
          </div>
          <div class="detail-row">
            <span>{{ copy.statusLabel }}</span>
            <b>{{ activeRecord ? activeRecord.statusText : "--" }}</b>
          </div>
          <div class="detail-row">
            <span>{{ copy.amountLabel }}</span>
            <b :class="activeRecord ? activeRecord.directionClass : ''">
              {{ activeRecord ? activeRecord.amountText : "--" }}
            </b>
          </div>
          <div class="detail-row">
            <span>{{ copy.submitTimeLabel }}</span>
            <b>{{ activeRecord ? activeRecord.timeText : "--" }}</b>
          </div>
          <div class="detail-row">
            <span>{{ copy.finishTimeLabel }}</span>
            <b>{{ activeRecord ? activeRecord.finishTimeText : "--" }}</b>
          </div>
        </div>
        <button type="button" class="modal-btn" @click="closeDetail">
          {{ copy.closeText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { getPageCopy } from "@/common/pageLocale";

function toNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

export default {
  data() {
    return {
      activeTab: "all",
      rechargeList: [],
      withdrawList: [],
      gameList: [],
      detailVisible: false,
      activeRecord: null,
    };
  },
  computed: {
    copy() {
      return getPageCopy(this, "account");
    },
    tabs() {
      return [
        { value: "all", label: this.copy.allTab },
        { value: "in", label: this.copy.incomeTab },
        { value: "out", label: this.copy.expenseTab },
      ];
    },
    mergedRecords() {
      const rechargeRecords = (this.rechargeList || []).map((item) =>
        this.normalizeRecord(item, "recharge")
      );
      const withdrawRecords = (this.withdrawList || []).map((item) =>
        this.normalizeRecord(item, "withdraw")
      );
      const gameRecords = (this.gameList || []).flatMap((item) =>
        this.normalizeGameRecords(item)
      );
      return [...rechargeRecords, ...withdrawRecords, ...gameRecords].sort((a, b) => {
        return (b.sortValue || 0) - (a.sortValue || 0);
      });
    },
    filteredRecords() {
      if (this.activeTab === "in") {
        return this.mergedRecords.filter((item) => item.direction === "in");
      }
      if (this.activeTab === "out") {
        return this.mergedRecords.filter((item) => item.direction === "out");
      }
      return this.mergedRecords;
    },
  },
  created() {
    if (!localStorage.getItem("token")) {
      this.$toast(this.copy.loginFirst);
      this.$router.replace("/Login");
      return;
    }
    this.bootstrap();
  },
  mounted() {
    document.title = `${this.copy.title} · OMN`;
  },
  methods: {
    async bootstrap() {
      await Promise.all([
        this.getUserRechargeList(),
        this.getUserWithdrawList(),
        this.getUserGameList(),
      ]);
    },
    async getUserRechargeList() {
      const res = await this.$http({
        method: "get",
        url: "user_get_recharge_list",
      });
      if (res && res.code === 200) {
        this.rechargeList = Array.isArray(res.data) ? res.data : [];
      }
    },
    async getUserWithdrawList() {
      const res = await this.$http({
        method: "get",
        url: "user_get_withdraw_list",
      });
      if (res && res.code === 200) {
        this.withdrawList = Array.isArray(res.data) ? res.data : [];
      }
    },
    async getUserGameList() {
      const res = await this.$http({
        method: "get",
        url: "user_get_game_list",
      });
      if (res && res.code === 200) {
        this.gameList = Array.isArray(res.data) ? res.data : [];
      }
    },
    normalizeRecord(item, type) {
      const isRecharge = type === "recharge";
      const amount = toNumber(item.money);
      const status = Number(item.status);
      return {
        ...item,
        id: item.id,
        type,
        title: isRecharge ? this.copy.rechargeTitle : this.copy.withdrawTitle,
        typeText: isRecharge ? this.copy.rechargeText : this.copy.withdrawText,
        direction: isRecharge ? "in" : "out",
        directionClass: isRecharge ? "income" : "expense",
        amountText: `${isRecharge ? "+" : "-"}${this.formatMoney(amount)}`,
        statusText: this.getLedgerStatusText(status),
        statusClass: this.getStatusClass(status),
        timeText: item.create_time || "--",
        finishTimeText: item.update_time || "--",
        sortValue: this.toTimestamp(item.create_time),
      };
    },
    normalizeGameRecords(item) {
      const betAmount = toNumber(item.money);
      const rebateAmount = toNumber(item.profit);
      const baseStatus = Number(item.status);
      const baseSortValue = this.toTimestamp(item.create_time);
      const records = [
        {
          ...item,
          id: `bet-${item.id}`,
          type: "bet",
          title: this.copy.betTitle,
          typeText: this.copy.betText,
          direction: "out",
          directionClass: "expense",
          amountText: `-${this.formatMoney(betAmount)}`,
          statusText: this.getGameStatusText(baseStatus),
          statusClass: baseStatus === 1 ? "ok" : "wait",
          timeText: item.create_time || "--",
          finishTimeText: item.update_time || "--",
          sortValue: baseSortValue,
        },
      ];

      if (Number(item.is_win) === 1 && rebateAmount > 0) {
        records.push({
          ...item,
          id: `rebate-${item.id}`,
          type: "rebate",
          title: this.copy.rebateTitle,
          typeText: this.copy.rebateText,
          direction: "in",
          directionClass: "income",
          amountText: `+${this.formatMoney(rebateAmount)}`,
          statusText: this.getGameWinText(Number(item.is_win)),
          statusClass: "ok",
          timeText: item.update_time || item.create_time || "--",
          finishTimeText: item.update_time || "--",
          sortValue: this.toTimestamp(item.update_time || item.create_time),
        });
      }

      return records;
    },
    getStatusClass(status) {
      if (status === 2 || status === 4) {
        return "ok";
      }
      if (status === 3 || status === 5) {
        return "bad";
      }
      return "wait";
    },
    getLedgerStatusText(status) {
      if (status === 2) {
        return this.copy.reviewSuccessText || this.copy.pendingText;
      }
      if (status === 3) {
        return this.copy.reviewReturnedText || this.copy.pendingText;
      }
      if (status === 4) {
        return this.copy.transferSuccessText || this.copy.reviewSuccessText || this.copy.pendingText;
      }
      if (status === 5) {
        return this.copy.transferFailedText || this.copy.reviewReturnedText || this.copy.pendingText;
      }
      return this.copy.pendingText;
    },
    getGameStatusText(status) {
      if (status === 1) {
        return this.copy.settledText || this.copy.pendingText;
      }
      return this.copy.pendingText;
    },
    getGameWinText(isWin) {
      if (isWin === 1) {
        return this.copy.winText || this.copy.settledText || this.copy.pendingText;
      }
      if (isWin === 2) {
        return this.copy.loseText || this.copy.pendingText;
      }
      return this.copy.pendingText;
    },
    formatMoney(value) {
      return toNumber(value).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    toTimestamp(value) {
      const time = new Date(value).getTime();
      return Number.isFinite(time) ? time : 0;
    },
    back() {
      window.history.back();
    },
    goHome() {
      this.$router.push("/Home");
    },
    openDetail(item) {
      this.activeRecord = item;
      this.detailVisible = true;
    },
    closeDetail() {
      this.detailVisible = false;
      this.activeRecord = null;
    },
  },
};
</script>

<style lang="less" scoped>
.account-page {
  min-height: 100vh;
  min-height: 100dvh;
  background:
    radial-gradient(circle at 100% 0, rgba(255, 63, 120, 0.11), transparent 28%),
    radial-gradient(circle at 0 14%, rgba(165, 92, 255, 0.1), transparent 26%),
    #0a0710;
  color: #fff7fb;
}

.account-shell {
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  max-width: none;
  margin: 0;
}

.account-head {
  position: sticky;
  top: 0;
  z-index: 20;
  display: grid;
  grid-template-columns: 2.625rem 1fr 2.625rem;
  align-items: center;
  min-height: 4.1875rem;
  padding: max(0.5rem, env(safe-area-inset-top)) 0.8125rem 0.5rem;
  background: rgba(10, 7, 16, 0.92);
  border-bottom: 1px solid rgba(255, 255, 255, 0.085);
  backdrop-filter: blur(1.125rem);
  -webkit-backdrop-filter: blur(1.125rem);
}

.head-btn {
  width: 2.375rem;
  height: 2.375rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.085);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.045);
  color: #fff;
}

.head-btn svg {
  width: 1.1875rem;
  height: 1.1875rem;
}

.head-copy h1 {
  margin: 0;
  text-align: center;
  font-size: 1.125rem;
  font-weight: 900;
  letter-spacing: -0.0125rem;
}

.head-copy p {
  margin: 0.1875rem 0 0;
  text-align: center;
  color: #a89baa;
  font-size: 0.5625rem;
  line-height: 1.4;
}

.account-content {
  padding: 1rem;
}

.segmented {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.3125rem;
  padding: 0.3125rem;
  margin-bottom: 0.875rem;
  border-radius: 1.125rem;
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid rgba(255, 255, 255, 0.085);
}

.segmented button {
  height: 2.375rem;
  border: 0;
  border-radius: 0.875rem;
  background: transparent;
  color: #9e919c;
  font-size: 0.75rem;
  font-weight: 900;
}

.segmented button.active {
  background: linear-gradient(90deg, #602a4d, #3e2145);
  color: #fff;
  box-shadow: 0 0.375rem 0.9375rem rgba(0, 0, 0, 0.16);
}

.empty-state {
  min-height: calc(100vh - 11rem);
  min-height: calc(100dvh - 11rem);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  color: #a596a2;
}

.empty-icon {
  width: 4.0625rem;
  height: 4.0625rem;
  margin-bottom: 0.75rem;
  border-radius: 1.125rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid rgba(255, 255, 255, 0.085);
  color: #7a4a62;
}

.empty-icon svg {
  width: 1.75rem;
  height: 1.75rem;
}

.empty-state p {
  margin: 0;
  font-size: 0.8125rem;
}

.record-list {
  display: flex;
  flex-direction: column;
  gap: 0.6875rem;
}

.record-card {
  width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.085);
  border-radius: 1.375rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.045);
  box-shadow: 0 0.8125rem 2.125rem rgba(0, 0, 0, 0.15);
  color: #fff7fb;
  text-align: left;
}

.record-head-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.625rem;
  padding-bottom: 0.625rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.085);
}

.record-head-row b {
  font-size: 0.8125rem;
  line-height: 1.4;
}

.record-status {
  height: 1.5rem;
  border-radius: 0.75rem;
  padding: 0 0.5rem;
  display: inline-flex;
  align-items: center;
  font-size: 0.5625rem;
  font-weight: 900;
  background: rgba(255, 255, 255, 0.065);
  color: #c9bdc7;
  white-space: nowrap;
}

.record-status.ok {
  background: rgba(77, 227, 165, 0.1);
  color: #67e7b3;
}

.record-status.wait {
  background: rgba(255, 198, 92, 0.1);
  color: #ffd071;
}

.record-status.bad {
  background: rgba(255, 63, 120, 0.1);
  color: #ff85a8;
}

.record-lines {
  padding-top: 0.4375rem;
}

.record-line {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.875rem;
  padding: 0.3125rem 0;
  font-size: 0.625rem;
}

.record-line span {
  color: #a89baa;
  flex: 0 0 auto;
}

.record-line b {
  color: #eee5ec;
  font-size: 0.625rem;
  font-weight: 750;
  text-align: right;
  word-break: break-word;
}

.record-line b.income,
.detail-row b.income {
  color: #67e7b3;
}

.record-line b.expense,
.detail-row b.expense {
  color: #ff8bad;
}

.modal-mask {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  background: rgba(3, 2, 5, 0.76);
  backdrop-filter: blur(0.5625rem);
}

.modal-card {
  width: 100%;
  max-width: 24.375rem;
  border-radius: 1.5rem;
  padding: 1.25rem;
  background: #1a1221;
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 1.375rem 4.375rem rgba(0, 0, 0, 0.5);
}

.modal-card h3 {
  margin: 0 0 0.625rem;
  font-size: 1.125rem;
  font-weight: 900;
}

.detail-list {
  margin-top: 0.25rem;
}

.detail-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.6875rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  font-size: 0.8125rem;
  line-height: 1.45;
}

.detail-row:last-child {
  border-bottom: 0;
}

.detail-row span {
  color: #a89baa;
  flex: 0 0 auto;
}

.detail-row b {
  text-align: right;
  font-weight: 800;
  color: #fff7fb;
  word-break: break-word;
}

.modal-btn {
  width: 100%;
  height: 2.6875rem;
  margin-top: 1rem;
  border: 0;
  border-radius: 0.9375rem;
  background: linear-gradient(135deg, #ff6d9c, #ff3f78);
  color: #fff;
  font-size: 0.9375rem;
  font-weight: 900;
}

button {
  cursor: pointer;
}

@media (min-width: 48rem) {
  .account-shell {
    max-width: 25rem;
    margin: 0 auto;
  }
}
</style>
